import type { Theme } from '@nivo/core'

export const COMPLETION_METHOD_COLORS: Record<string, string> = {
  DN_FORM: '#77BEE7',      // brand-300
  PHONE: '#47CD89',        // success-400
  FAX: '#FEC84B',          // warning-300
  EMAIL_EXTERNAL: '#FDB022', // warning-400
  ATS_PLATFORM: '#FDA29B', // error-300
  NO_RESPONSE: '#D0D5DD',  // gray-300
  UNKNOWN: '#EAECF0',      // gray-200
}

/** Friendly labels for each completion method — single source of truth */
export const COMPLETION_METHOD_LABELS: Record<string, string> = {
  dnForm: 'Double Nickel',
  phone: 'Phone',
  fax: 'Fax',
  emailExternal: 'Email',
  atsPlatform: 'ATS',
  noResponse: 'No Response',
  unknown: 'Unknown',
}

/** Bar chart keys (camelCase, matching data field names) */
export const STACKED_BAR_KEYS = [
  'dnForm',
  'phone',
  'fax',
  'emailExternal',
  'atsPlatform',
  'noResponse',
  'unknown',
] as const

/** Map each bar key to its color */
export const STACKED_BAR_COLORS: Record<string, string> = {
  dnForm: COMPLETION_METHOD_COLORS.DN_FORM,
  phone: COMPLETION_METHOD_COLORS.PHONE,
  fax: COMPLETION_METHOD_COLORS.FAX,
  emailExternal: COMPLETION_METHOD_COLORS.EMAIL_EXTERNAL,
  atsPlatform: COMPLETION_METHOD_COLORS.ATS_PLATFORM,
  noResponse: COMPLETION_METHOD_COLORS.NO_RESPONSE,
  unknown: COMPLETION_METHOD_COLORS.UNKNOWN,
}

/** Autonomy split — who did the work on an answered verification */
export const AUTONOMY_KEYS = ['autonomous', 'assisted', 'manual'] as const

export const AUTONOMY_LABELS: Record<string, string> = {
  autonomous: 'Automated',
  assisted: 'Assisted',
  manual: 'Manual',
}

export const AUTONOMY_COLORS: Record<string, string> = {
  autonomous: '#77BEE7', // brand-300, matches Automated in the outreach breakdown
  assisted: '#B9E6FE', // light brand, matches Assisted in the outreach breakdown
  manual: '#98A2B3', // gray-400, matches Manual in the outreach breakdown
}

/** Shown under the chart so the numbers can't be read two ways */
export const AUTONOMY_DEFINITIONS: Record<string, string> = {
  autonomous:
    'Every outreach touch before the answer was automated (a scheduled email step or a scheduled voice-agent call), and the answer reached Double Nickel directly (the Double Nickel form or a verified voice-agent call).',
  assisted:
    'Double Nickel automation did part of the work. A recruiter launched a voice-agent call, or automated outreach ran but a person also reached out or recorded the answer.',
  manual:
    'Only recruiter outreach (their own emails, calls, faxes or ATS requests), or the answer arrived with no logged outreach.',
}

export const chartTheme: Theme = {
  axis: {
    ticks: {
      text: {
        fontSize: 12,
        fill: '#374151',
      },
      line: {
        stroke: 'transparent',
      },
    },
    domain: {
      line: {
        stroke: '#e5e7eb',
        strokeWidth: 1,
      },
    },
    legend: {
      text: {
        fontSize: 12,
        fill: '#374151',
        fontWeight: 600,
      },
    },
  },
  grid: {
    line: {
      stroke: 'transparent',
    },
  },
  tooltip: {
    container: {
      background: '#ffffff',
      borderRadius: '8px',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
      padding: '8px 12px',
      fontSize: '13px',
    },
  },
  labels: {
    text: {
      fontSize: 11,
      fill: '#374151',
    },
  },
}

// Re-export shared formatting utilities for convenience
export { formatMonth, formatPercent, formatDays } from 'src/lib/formatters'
